import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Stack,
  Card,
  CardContent,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Divider,
  Fab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import { useOffline } from "../contexts/OfflineContext";
import { apiGet, apiPost, apiDelete } from "../utils/apiClient";
import { HEADER_TEXT } from "../App";

export default function FamilyManager({ tripId, families, members, isOwner, createdBy, onFamiliesChange }) {
  const { refreshCount } = useOffline();
  const [addFamilyOpen, setAddFamilyOpen]       = useState(false);
  const [addTravelerOpen, setAddTravelerOpen]   = useState(false);
  const [addMemberOpen, setAddMemberOpen]       = useState(null); // family id
  const [familyName, setFamilyName]             = useState("");
  const [travelerName, setTravelerName]         = useState("");
  const [selectedUserId, setSelectedUserId]     = useState("");
  const [selectedGuestId, setSelectedGuestId]   = useState("");
  const [deleteConfirm, setDeleteConfirm]       = useState(null);
  const [deleteError, setDeleteError]           = useState("");
  const [actionError, setActionError]           = useState("");

  const refreshFamilies = async () => {
    const data = await apiGet(`/api/trips/${tripId}/families`);
    onFamiliesChange(data);
  };

  const closeAddDialog = () => {
    setAddMemberOpen(null);
    setSelectedUserId("");
    setSelectedGuestId("");
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAddFamily = async () => {
    const data = await apiPost(`/api/trips/${tripId}/families`, { name: familyName });
    await refreshCount();
    if (!data._queued) onFamiliesChange([...families, data]);
    setFamilyName("");
    setAddFamilyOpen(false);
  };

  const handleAddTraveler = async () => {
    try {
      const data = await apiPost(`/api/trips/${tripId}/families/traveler`, { name: travelerName.trim() });
      onFamiliesChange([...families, data]);
      setTravelerName("");
      setAddTravelerOpen(false);
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to add traveler.");
    }
  };

  const handleDeleteFamily = async () => {
    try {
      await apiDelete(`/api/trips/${tripId}/families/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      setDeleteError("");
      await refreshFamilies();
    } catch (err) {
      setDeleteError(err.response?.data?.error || "Failed to delete family.");
    }
  };

  const handleAddMemberOrGuest = async () => {
    try {
      if (selectedUserId) {
        await apiPost(`/api/trips/${tripId}/families/${addMemberOpen}/members`, { userId: selectedUserId });
      } else if (selectedGuestId) {
        await apiPost(`/api/trips/${tripId}/families/${addMemberOpen}/guests`, { guestId: selectedGuestId });
      }
      await refreshFamilies();
      closeAddDialog();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to add member.");
    }
  };

  const handleRemoveMember = async (familyId, userId) => {
    try {
      await apiDelete(`/api/trips/${tripId}/families/${familyId}/members/${userId}`);
      await refreshFamilies();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to remove member.");
    }
  };

  const handleRemoveGuest = async (familyId, guestId) => {
    try {
      await apiDelete(`/api/trips/${tripId}/families/${familyId}/guests/${guestId}`);
      await refreshFamilies();
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to remove guest.");
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  const currentFamilyMemberIds = new Set(
    (families.find((f) => f.id === addMemberOpen)?.members || []).map((m) => m.id)
  );
  const assignedUserIds = new Set(
    families.filter((f) => !f.is_personal).flatMap((f) => (f.members || []).map((m) => m.id))
  );
  const activeMemberIds = new Set(
    families.flatMap((f) => (f.members || []).map((m) => m.id))
  );
  const availableUsers = members.filter(
    (m) => activeMemberIds.has(m.id) && !currentFamilyMemberIds.has(m.id) && !assignedUserIds.has(m.id)
  );

  // Guests sitting in personal families that can be moved into a group family
  const currentFamilyGuestIds = new Set(
    (families.find((f) => f.id === addMemberOpen)?.guests || []).map((g) => g.id)
  );
  const assignedGuestIds = new Set(
    families.filter((f) => !f.is_personal).flatMap((f) => (f.guests || []).map((g) => g.id))
  );
  const availablePersonalGuests = families
    .filter((f) => f.is_personal && (!f.members || f.members.length === 0))
    .flatMap((f) => (f.guests || []).map((g) => ({ ...g, personalFamilyId: f.id })))
    .filter((g) => !currentFamilyGuestIds.has(g.id) && !assignedGuestIds.has(g.id));

  const canAdd = Boolean(selectedUserId) || Boolean(selectedGuestId);

  return (
    <Box sx={{ pb: 12 }}>
      {families.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
          No families yet.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {families.map((fam) => {
            const famMembers = fam.members || [];
            const famGuests  = fam.guests  || [];
            const hasNobody  = famMembers.length === 0 && famGuests.length === 0;

            return (
              <Card key={fam.id} variant="outlined">
                <CardContent sx={{ pb: "12px !important" }}>
                  {fam.is_personal ? (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {famMembers.map((m) => (
                          <Chip
                            key={`u-${m.id}`}
                            label={m.name}
                            size="small"
                            avatar={<Avatar src={m.avatar_url}>{m.name[0]}</Avatar>}
                          />
                        ))}
                        {famGuests.map((g) => (
                          <Chip
                            key={`g-${g.id}`}
                            label={g.name}
                            size="small"
                            variant="outlined"
                            icon={<PersonIcon fontSize="small" />}
                            sx={{ color: "text.secondary", borderStyle: "dashed" }}
                          />
                        ))}
                      </Box>
                      {isOwner && !famMembers.some((m) => m.id === createdBy) && (
                        <IconButton size="small" color="error"
                          onClick={() => { setDeleteConfirm(fam); setDeleteError(""); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ) : (
                  <>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography fontWeight="bold">{fam.name}</Typography>
                    {isOwner && (
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => { setAddMemberOpen(fam.id); setSelectedUserId(""); setSelectedGuestId(""); }}
                        >
                          <PersonAddIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => { setDeleteConfirm(fam); setDeleteError(""); }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                    {hasNobody ? (
                      <Typography variant="caption" color="text.disabled">No members</Typography>
                    ) : (
                      <>
                        {famMembers.map((m) => (
                          <Chip
                            key={`u-${m.id}`}
                            label={m.name}
                            size="small"
                            avatar={<Avatar src={m.avatar_url}>{m.name[0]}</Avatar>}
                            onDelete={isOwner ? () => handleRemoveMember(fam.id, m.id) : undefined}
                          />
                        ))}
                        {famGuests.map((g) => (
                          <Chip
                            key={`g-${g.id}`}
                            label={g.name}
                            size="small"
                            variant="outlined"
                            icon={<PersonIcon fontSize="small" />}
                            sx={{ color: "text.secondary", borderStyle: "dashed" }}
                            onDelete={isOwner ? () => handleRemoveGuest(fam.id, g.id) : undefined}
                          />
                        ))}
                      </>
                    )}
                  </Box>
                  </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {isOwner && (
        <Button
          variant="outlined"
          startIcon={<PersonIcon />}
          fullWidth
          sx={{ mt: 2 }}
          onClick={() => { setTravelerName(""); setAddTravelerOpen(true); }}
        >
          Add Traveler
        </Button>
      )}

      {isOwner && (
        <Fab color="primary" sx={{ position: "fixed", bottom: 32, right: 24, zIndex: 1100 }}
          onClick={() => { setFamilyName(""); setAddFamilyOpen(true); }}>
          <AddIcon sx={{ color: HEADER_TEXT }} />
        </Fab>
      )}

      {/* ── Add family dialog ─────────────────────────────────────────── */}
      <Dialog open={addFamilyOpen} onClose={() => setAddFamilyOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New Family</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Family name"
            fullWidth
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddFamilyOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddFamily} disabled={!familyName.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add traveler dialog ───────────────────────────────────────── */}
      <Dialog open={addTravelerOpen} onClose={() => setAddTravelerOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New Traveler</DialogTitle>
        <DialogContent>
          {actionError && <Alert severity="error" sx={{ mb: 1 }}>{actionError}</Alert>}
          <TextField
            autoFocus
            label="Name"
            placeholder="e.g. Grandma, Uncle Tom…"
            fullWidth
            value={travelerName}
            onChange={(e) => setTravelerName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAddTravelerOpen(false); setActionError(""); }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddTraveler} disabled={!travelerName.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete family confirm dialog ──────────────────────────────── */}
      <Dialog
        open={Boolean(deleteConfirm)}
        onClose={() => { setDeleteConfirm(null); setDeleteError(""); }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{deleteConfirm?.is_personal ? "Remove Traveler" : "Delete Family"}</DialogTitle>
        <DialogContent>
          {deleteError ? (
            <Alert severity="error">{deleteError}</Alert>
          ) : deleteConfirm?.is_personal ? (
            <Typography>
              Remove <strong>{deleteConfirm?.guests?.[0]?.name ?? deleteConfirm?.members?.[0]?.name ?? deleteConfirm?.name}</strong> from the trip? This cannot be undone.
            </Typography>
          ) : (
            <Typography>
              Delete family <strong>{deleteConfirm?.name}</strong>? This cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDeleteConfirm(null); setDeleteError(""); }}>
            {deleteError ? "Close" : "Cancel"}
          </Button>
          {!deleteError && (
            <Button variant="contained" color="error" onClick={handleDeleteFamily}>
              {deleteConfirm?.is_personal ? "Remove" : "Delete"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Add member / guest dialog ─────────────────────────────────── */}
      <Dialog open={Boolean(addMemberOpen)} onClose={() => { closeAddDialog(); setActionError(""); }} fullWidth maxWidth="xs">
        <DialogTitle>Add to Family</DialogTitle>
        <DialogContent>
          {actionError && <Alert severity="error" sx={{ mb: 1 }}>{actionError}</Alert>}
          {availableUsers.length === 0 && availablePersonalGuests.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No unassigned members or travelers available.
            </Typography>
          ) : (
            <TextField
              select
              label="Select member or traveler"
              fullWidth
              value={selectedUserId || selectedGuestId}
              onChange={(e) => {
                const val = e.target.value;
                const isGuest = availablePersonalGuests.some((g) => String(g.id) === String(val));
                if (isGuest) { setSelectedGuestId(val); setSelectedUserId(""); }
                else { setSelectedUserId(val); setSelectedGuestId(""); }
              }}
              sx={{ mt: 1 }}
            >
              {availableUsers.map((m) => (
                <MenuItem key={`u-${m.id}`} value={m.id}>{m.name}</MenuItem>
              ))}
              {availablePersonalGuests.length > 0 && availableUsers.length > 0 && (
                <Divider />
              )}
              {availablePersonalGuests.map((g) => (
                <MenuItem key={`g-${g.id}`} value={g.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon fontSize="small" sx={{ color: "text.disabled" }} />
                    {g.name}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAddDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleAddMemberOrGuest} disabled={!canAdd}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(actionError)}
        autoHideDuration={4000}
        onClose={() => setActionError("")}
        message={actionError}
      />
    </Box>
  );
}
